import { MarketplacePropertyCategory_ListingMediaState as ListingMediaState } from '@opendoor/observability/pbs/journey/data/categories/marketplace_property_category_pb';

/**
 * Enhanced media a listing carries, as far as the marketplace read path can
 * report it. This is the only input to slot ownership: eligibility is a
 * property of the inventory, never of the viewer.
 */
export type ListingMediaAvailability = {
  hasWalkthroughVideo: boolean;
  hasTour3d: boolean;
};

/**
 * One experiment's claim on the PDP media slot.
 *
 * `armStates` maps an assigned arm to the state the slot renders for it. It is
 * a map rather than a boolean so a claim can add arms without the slot
 * learning about them, and an arm missing from it renders `SLOT_FALLBACK_STATE`
 * — an arm shipped in the platform ahead of the client degrades to photos
 * instead of rendering nothing.
 */
export type MediaSlotClaim = {
  readonly experimentKey: string;
  readonly claims: (availability: ListingMediaAvailability) => boolean;
  readonly armStates: Readonly<Record<string, ListingMediaState>>;
};

/** State the slot holds for an unassigned listing, and for an arm it cannot map. */
export const SLOT_FALLBACK_STATE = ListingMediaState.PHOTOS;

export const PDP_HERO_VIDEO_EXPERIMENT_KEY = 'resale-pdp-hero-walkthrough-video';
/**
 * Experiment claims take precedence over the default 3D tour so overlapping
 * inventory does not change the walkthrough-video experiment's control.
 */
export const PDP_MEDIA_SLOT_CLAIMS: readonly MediaSlotClaim[] = [
  {
    experimentKey: PDP_HERO_VIDEO_EXPERIMENT_KEY,
    claims: (availability) => availability.hasWalkthroughVideo,
    armStates: {
      control: ListingMediaState.PHOTOS,
      treatment: ListingMediaState.VIDEO,
    },
  },
];

/** The claim that owns this listing's media slot, or null when none does. */
export const resolveMediaSlotClaim = (
  availability: ListingMediaAvailability,
  claims: readonly MediaSlotClaim[] = PDP_MEDIA_SLOT_CLAIMS,
): MediaSlotClaim | null => claims.find((claim) => claim.claims(availability)) ?? null;

/** State the slot renders for `arm`, falling back to photos for an unmapped or absent arm. */
export const resolveMediaState = (
  claim: MediaSlotClaim | null,
  arm: string | null,
): ListingMediaState => {
  if (!claim || arm == null) {
    return SLOT_FALLBACK_STATE;
  }
  // Own-property check, not a plain index: `arm` is an operator-chosen variation
  // name arriving from the platform, and a bare lookup for one that collides
  // with `Object.prototype` — `constructor`, `toString` — resolves to an
  // inherited function that `??` does not catch, returning a non-state where the
  // signature promises one.
  return Object.hasOwn(claim.armStates, arm)
    ? (claim.armStates[arm] ?? SLOT_FALLBACK_STATE)
    : SLOT_FALLBACK_STATE;
};

/** Baseline for a listing carrying no enhanced media, and the shape a claim's absence resolves to. */
export const NO_ENHANCED_MEDIA: ListingMediaAvailability = {
  hasWalkthroughVideo: false,
  hasTour3d: false,
};
